import React, { useState, useEffect } from 'react';
import Select from '../ui/Select';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import axiosInstance from '../../api/axiosInstance';
import { toast } from 'react-toast';

const SecuritySettings = () => {
  const [enableMfaCode, setEnableMfaCode] = useState('');
  const [showEnableMfaInput, setShowEnableMfaInput] = useState(false);
  const [mfaStatus, setMfaStatus] = useState({
    mfaSecretPresent: false,
    mfaEnabled: false
  });
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });

  const fetchMFAStatus = async () => {
    try {
      const response = await axiosInstance.get('/users/mfa_status');
      const { mfa_secret_present, mfa_enabled } = response.detail;
      setMfaStatus({
        mfaSecretPresent: mfa_secret_present,
        mfaEnabled: mfa_enabled
      });
      return { mfa_secret_present, mfa_enabled };
    } catch (error) {
      console.error('Error fetching MFA status:', error);
      toast.error('Failed to fetch MFA status');
      return null;
    }
  };

  useEffect(() => {
    fetchMFAStatus();
  }, []);
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorAuth: false,
    sessionTimeout: '30',
    passwordExpiryDays: '90',
    loginAttempts: '5',
    rememberDevice: true,
    ipRestriction: false,
    activityLogging: true
  });

  const [mfaSetup, setMfaSetup] = useState({
    showQRCode: false,
    qrCodeData: null,
    mfaCode: '',
    isLoading: false,
    error: null
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});

  const handleToggleChange = (e) => {
    const { name, checked } = e.target;
    setSecuritySettings({
      ...securitySettings,
      [name]: checked
    });
  };

  const handleSelectChange = async (e) => {
    e.preventDefault();
    const { name, value } = e.target;
    
    if (name === 'sessionTimeout') {
      try {
        const response = await axiosInstance.post(
          '/users/session_time',
          {
            hours: parseInt(value) / 60
          },
          { skipGlobalToast: true }
        );
        if (response.code === 200 || response.status_code === 200 || response.status === 200) {
          toast.success(
            response.detail?.message || response.message || 'Session timeout updated successfully'
          );
        } else {
          toast.error(
            response.detail?.message || response.message || 'Failed to update session timeout'
          );
          return;
        }
      } catch (error) {
        console.error('Error updating session timeout:', error);
        const errorMessage =
          error.response?.data?.detail?.message ||
          error.response?.data?.message ||
          error.response?.data?.detail ||
          'Failed to update session timeout';
        toast.error(
          typeof errorMessage === 'string' ? errorMessage : 'Failed to update session timeout'
        );
        return;
      }
    } else if (name === 'passwordExpiryDays') {
      try {
        const numValue = value === '0' ? -1 : parseInt(value);
        const response = await axiosInstance.post(`/user_profile/update_password_expiry?password_expiry_days=${numValue}`);
        if (response.status === 200) {
          toast.success(response.data?.message || 'Password expiry updated successfully');
        }
      } catch (error) {
        console.error('Error updating password expiry:', error);
        toast.error('Failed to update password expiry');
      }
    } else if (name === 'loginAttempts') {
      try {
        const response = await axiosInstance.post(`/user_profile/add_login_attempts?maximum_attempts=${parseInt(value)}`);
        if (response.status === 200) {
          toast.success(response.data?.message || 'Maximum login attempts updated successfully');
        }
      } catch (error) {
        console.error('Error updating login attempts:', error);
        toast.error('Failed to update login attempts');
      }
    }
    
    setSecuritySettings((prev) => ({
      ...prev,
      [name]: value
    }));
  };

useEffect(() => {
  const fetchSecuritySettings = async () => {
    try {
      const response = await axiosInstance.get('/user_profile/get_security_settings');
      if (response.code === 200 && response?.detail) {
        const { session_hours, password_expiry_days, maximum_attempts } = response?.detail;
        
        const sessionTimeoutValue = (session_hours * 60).toString();
        const passwordExpiryValue = password_expiry_days === -1 ? '0' : password_expiry_days.toString();
        const loginAttemptsValue = maximum_attempts.toString();

        setSecuritySettings(prev => ({
          ...prev,
          sessionTimeout: sessionTimeoutValue,
          passwordExpiryDays: passwordExpiryValue,
          loginAttempts: loginAttemptsValue
        }));
      }
    } catch (error) {
      console.error('Error fetching security settings:', error);
      toast.error('Failed to load security settings');
    }
  };

  fetchSecuritySettings();
}, []);


  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value
    });

    setErrors((prev) => {
      const next = { ...prev, [name]: null };
      if (name === 'newPassword' || name === 'confirmPassword') {
        next.newPassword = null;
        next.confirmPassword = null;
      }
      return next;
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const getPasswordFormErrors = () => {
    const newErrors = {};

    if (!passwordData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!passwordData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }

    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    return newErrors;
  };

  const isPasswordFormValid = Object.keys(getPasswordFormErrors()).length === 0;

  const getVisiblePasswordErrors = () => {
    const visibleErrors = {};

    if (errors.currentPassword) {
      visibleErrors.currentPassword = errors.currentPassword;
    }

    if (passwordData.newPassword && passwordData.newPassword.length < 8) {
      visibleErrors.newPassword = 'Password must be at least 8 characters';
    } else if (errors.newPassword) {
      visibleErrors.newPassword = errors.newPassword;
    }

    if (
      passwordData.confirmPassword &&
      passwordData.newPassword !== passwordData.confirmPassword
    ) {
      visibleErrors.confirmPassword = 'New password and confirm password do not match';
    } else if (errors.confirmPassword) {
      visibleErrors.confirmPassword = errors.confirmPassword;
    }

    return visibleErrors;
  };

  const visiblePasswordErrors = getVisiblePasswordErrors();

  const validatePasswordForm = () => {
    const newErrors = getPasswordFormErrors();
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!validatePasswordForm()) {
      return;
    }

    try {
      const response = await axiosInstance.post(
        `/users/secure_change_password`,
        {
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        },
        { skipGlobalToast: true }
      );

      if (response.code === 200 || response.status_code === 200) {
        toast.success(
          response.detail?.message || response.message || 'Password changed successfully'
        );
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setErrors({});
      } else {
        toast.error(
          response.detail?.message || response.message || 'Failed to change password'
        );
      }
    } catch (error) {
      console.error('Error changing password:', error);
      const errorMessage =
        error.response?.data?.detail?.message ||
        error.response?.data?.message ||
        error.response?.data?.detail ||
        'Failed to change password';
      toast.error(
        typeof errorMessage === 'string' ? errorMessage : 'Failed to change password'
      );
    }
  };

  const handleResetMFA = async () => {
    setMfaSetup(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await axiosInstance.post('/users/reset_mfa_secret', null, {
        skipGlobalToast: true
      });
      if (response.status_code === 200) {
        toast.success(response.message);

        await fetchMFAStatus();

        const qrResponse = await axiosInstance.post('/users/setup_mfa', null, {
          responseType: 'arraybuffer',
          skipGlobalToast: true,
        });

        if (!qrResponse) {
          throw new Error('No data received from server');
        }

        const base64String = btoa(
          new Uint8Array(qrResponse)
            .reduce((data, byte) => data + String.fromCharCode(byte), '')
        );

        const qrCodeUrl = `data:image/png;base64,${base64String}`;

        setMfaSetup(prev => ({
          ...prev,
          showQRCode: true,
          qrCodeData: qrCodeUrl,
          isLoading: false,
        }));
      }
    } catch (error) {
      console.error('MFA reset error:', error);
      setMfaSetup(prev => ({
        ...prev,
        error: error.message || 'Failed to reset MFA. Please try again.',
        isLoading: false,
      }));
      toast.error('Failed to reset MFA');
    }
  };

  const handleSetupMFA = async () => {
    setMfaSetup(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await axiosInstance.post('/users/setup_mfa', null, {
        responseType: 'arraybuffer',
      });

      if (!response) {
        throw new Error('No data received from server');
      }

      const base64String = btoa(
        new Uint8Array(response)
          .reduce((data, byte) => data + String.fromCharCode(byte), '')
      );

      const qrCodeUrl = `data:image/png;base64,${base64String}`;

      setMfaSetup(prev => ({
        ...prev,
        showQRCode: true,
        qrCodeData: qrCodeUrl,
        isLoading: false,
      }));

    } catch (error) {
      console.error('MFA setup error:', error);
      setMfaSetup(prev => ({
        ...prev,
        error: error.message || 'Failed to setup MFA. Please try again.',
        isLoading: false,
      }));
    }
  };


  const handleVerifyMFA = async () => {
    if (!mfaSetup.mfaCode || mfaSetup.mfaCode.length !== 6) {
      setMfaSetup(prev => ({ ...prev, error: 'Please enter a valid 6-digit code' }));
      return;
    }

    setMfaSetup(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await axiosInstance.post(`/users/enable_disable_mfa`, {
        mfa_code: mfaSetup.mfaCode
      });
       if (response.status_code === 200) {
        setMfaSetup({
          showQRCode: false,
          qrCodeData: null,
          mfaCode: '',
          isLoading: false,
          error: null
        });
        toast.success(response.message);
        await fetchMFAStatus();
      }
    } catch (error) {
      setMfaSetup(prev => ({
        ...prev,
        error: 'Failed to verify MFA code. Please try again.',
        isLoading: false
      }));
    }
  };

  const handleMFACodeChange = (e) => {
    const { value } = e.target;
    if (value.length <= 6 && /^\d*$/.test(value)) {
      setMfaSetup(prev => ({ ...prev, mfaCode: value }));
    }
  };

  const handleSecuritySubmit = (e) => {
    e.preventDefault();

    console.log('Security settings saved:', securitySettings);
    toast.success('Security settings updated successfully!');
  };

  const handleEnable2FA = () => {
    toast.info('2FA setup process would start here');
    setSecuritySettings({
      ...securitySettings,
      twoFactorAuth: true
    });
  };

  const [disablePassword, setDisablePassword] = useState('');
  const [showDisableMfaInput, setShowDisableMfaInput] = useState(false);

  const handleDisable2FA = () => {
    setShowDisableMfaInput(true);
  };

  const handleDisableMfaSubmit = async () => {
    if (!disablePassword) {
      toast.error('Please enter your password');
      return;
    }

    try {
      const response = await axiosInstance.post('/users/enable_disable_mfa', {
        password: disablePassword
      });

      if (response.status_code === 200) {
        toast.success(response.message);
        setShowDisableMfaInput(false);
        setDisablePassword('');

        const status = await fetchMFAStatus();
        if (status && !status.mfa_enabled) {
          setSecuritySettings(prev => ({ ...prev, twoFactorAuth: false }));
        }
      }
    } catch (error) {
      console.error('Error disabling MFA:', error);
      toast.error(error.response?.data?.message || 'Failed to disable MFA');
    }
  };

  const handleDisablePasswordChange = (e) => {
    const { value } = e.target;
    setDisablePassword(value);
  };

  const handleEnableMfaCodeChange = (e) => {
    const { value } = e.target;
    if (value.length <= 6 && /^\d*$/.test(value)) {
      setEnableMfaCode(value);
    }
  };

  const handleEnableMfaSubmit = async () => {
    if (!enableMfaCode || enableMfaCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setMfaSetup(prev => ({ ...prev, isLoading: true }));
    try {
      const response = await axiosInstance.post('/users/enable_disable_mfa', {
        mfa_code: enableMfaCode
      });

      if (response.status_code === 200) {
        toast.success(response.message);
        setShowEnableMfaInput(false);
        setEnableMfaCode('');

        await fetchMFAStatus();
      }
    } catch (error) {
      console.error('Error enabling MFA:', error);
      toast.error(error.response?.data?.message || 'Failed to enable MFA');
    } finally {
      setMfaSetup(prev => ({ ...prev, isLoading: false }));
    }
  };

  return (
    <div className="settings-section">
      <div className="settings-section-intro">
        <h2 className="settings-section-title">Security</h2>
        <p className="settings-subtitle">Password, sessions and account protection.</p>
      </div>

      <div className="settings-card">
        <h3 className="settings-card-title">Change Password</h3>
        <p className="settings-card-description">
          Update your password to keep your account secure.
        </p>

        <form onSubmit={handlePasswordSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="currentPassword">Current Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.currentPassword ? 'text' : 'password'}
                id="currentPassword"
                name="currentPassword"
                className={`form-input ${visiblePasswordErrors.currentPassword ? 'error' : ''}`}
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-visibility-toggle"
                onClick={() => togglePasswordVisibility('currentPassword')}
                aria-label={showPasswords.currentPassword ? 'Hide current password' : 'Show current password'}
              >
                {showPasswords.currentPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
              </button>
            </div>
            {visiblePasswordErrors.currentPassword && <div className="form-error">{visiblePasswordErrors.currentPassword}</div>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="newPassword">New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.newPassword ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                className={`form-input ${visiblePasswordErrors.newPassword ? 'error' : ''}`}
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-visibility-toggle"
                onClick={() => togglePasswordVisibility('newPassword')}
                aria-label={showPasswords.newPassword ? 'Hide new password' : 'Show new password'}
              >
                {showPasswords.newPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
              </button>
            </div>
            {visiblePasswordErrors.newPassword && <div className="form-error">{visiblePasswordErrors.newPassword}</div>}
            <div className="form-hint">Password must be at least 8 characters long and include a mix of letters, numbers, and symbols.</div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="confirmPassword">Confirm New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.confirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                className={`form-input ${visiblePasswordErrors.confirmPassword ? 'error' : ''}`}
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-visibility-toggle"
                onClick={() => togglePasswordVisibility('confirmPassword')}
                aria-label={showPasswords.confirmPassword ? 'Hide confirm password' : 'Show confirm password'}
              >
                {showPasswords.confirmPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
              </button>
            </div>
            {visiblePasswordErrors.confirmPassword && <div className="form-error">{visiblePasswordErrors.confirmPassword}</div>}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" className="btn btn-primary" disabled={!isPasswordFormValid}>Change Password</button>
          </div>
        </form>
      </div>

      <div className="settings-card">
        <h3 className="settings-card-title">Two-Factor Authentication</h3>
        <p className="settings-card-description">
          Add an extra layer of security to your account by requiring a verification code in addition to your password.
        </p>

        <div style={{ marginBottom: '1rem' }}>
          {!mfaSetup.showQRCode && !mfaStatus.mfaEnabled && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>Disabled</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-muted, #6c757d)' }}>
                    Enable two-factor authentication for enhanced security.
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  {mfaStatus.mfaSecretPresent && (
                    <>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleResetMFA}
                        disabled={mfaSetup.isLoading}
                      >
                        {mfaSetup.isLoading ? 'Resetting...' : 'Reset MFA'}
                      </button>
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        {showEnableMfaInput ? (
                          <>
                            <input
                              type="text"
                              className="form-input"
                              value={enableMfaCode}
                              onChange={handleEnableMfaCodeChange}
                              placeholder="Enter 6-digit code"
                              maxLength="6"
                            />
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleEnableMfaSubmit}
                              disabled={mfaSetup.isLoading || enableMfaCode.length !== 6}
                            >
                              {mfaSetup.isLoading ? 'Enabling...' : 'Submit'}
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setShowEnableMfaInput(true)}
                            disabled={mfaSetup.isLoading}
                          >
                            Enable MFA
                          </button>
                        )}
                      </div>
                    </>
                  )}
                  {!mfaStatus.mfaSecretPresent && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSetupMFA}
                      disabled={mfaSetup.isLoading}
                    >
                      {mfaSetup.isLoading ? 'Setting up...' : 'Setup MFA'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {mfaSetup.showQRCode && (
            <div>
              <div style={{ marginBottom: '1rem' }}>
                <h4>Setup Two-Factor Authentication</h4>
                <p>1. Scan the QR code below with your authenticator app</p>
                <div style={{
                  background: '#fff',
                  padding: '1rem',
                  borderRadius: '8px',
                  display: 'inline-block',
                  marginBottom: '1rem',
                  border: '1px solid #ddd'
                }}>
                  <img
                    src={mfaSetup.qrCodeData}
                    alt="MFA QR Code"
                    style={{
                      maxWidth: '200px',
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                </div>
                <p>2. Enter the 6-digit code from your authenticator app</p>
                <div style={{ maxWidth: '200px' }}>
                  <input
                    type="text"
                    className="form-input"
                    value={mfaSetup.mfaCode}
                    onChange={handleMFACodeChange}
                    placeholder="Enter 6-digit code"
                    maxLength="6"
                  />
                </div>
                {mfaSetup.error && (
                  <div style={{ color: 'var(--danger, #dc3545)', marginTop: '0.5rem' }}>
                    {mfaSetup.error}
                  </div>
                )}
                <div style={{ marginTop: '1rem' }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => handleVerifyMFA()}
                    disabled={mfaSetup.isLoading || mfaSetup.mfaCode.length !== 6}
                  >
                    {mfaSetup.isLoading ? 'Verifying...' : 'Submit'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {mfaStatus.mfaEnabled && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>Enabled</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-muted, #6c757d)' }}>
                    Your account is protected with two-factor authentication.
                  </div>
                </div>
                <button type="button" className="btn btn-outline-danger" onClick={handleDisable2FA}>
                  Disable
                </button>
              </div>

              {showDisableMfaInput && (
                <div style={{ marginTop: '1rem', maxWidth: '300px' }}>
                  <p style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                    Enter your password to disable MFA
                  </p>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <input
                      type="password"
                      className="form-input"
                      value={disablePassword}
                      onChange={handleDisablePasswordChange}
                      placeholder="Enter Password"
                    />
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={handleDisableMfaSubmit}
                      disabled={!disablePassword}
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <form onSubmit={handleSecuritySubmit}>
        <div className="settings-card">
          <h3 className="settings-card-title">Session & Login Settings</h3>
          <p className="settings-card-description">
            Configure how your login sessions are managed.
          </p>

          <div className="form-group">
            <label className="form-label" htmlFor="sessionTimeout">Session Timeout</label>
            <Select
              id="sessionTimeout"
              name="sessionTimeout"
              className="rt-select--form"
              value={securitySettings.sessionTimeout}
              onChange={handleSelectChange}
            >
              {/* <option value="15">15 minutes</option>
              <option value="30">30 minutes</option> */}
              <option value="60">1 hour</option>
              <option value="120">2 hours</option>
              <option value="240">4 hours</option>
              <option value="480">8 hours</option>
              <option value="1440">1 day</option>
              <option value="4320">3 day</option>
              <option value="10080">7 day</option>

            </Select>
            <div className="form-hint">Your session will expire after this period of inactivity.</div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="passwordExpiryDays">Password Expiry (days)</label>
            <Select
              id="passwordExpiryDays"
              name="passwordExpiryDays"
              className="rt-select--form"
              value={securitySettings.passwordExpiryDays}
              onChange={handleSelectChange}
            >
              <option value="30">30 days</option>
              <option value="60">60 days</option>
              <option value="90">90 days</option>
              <option value="180">180 days</option>
              <option value="365">365 days</option>
              <option value="0">Never</option>
            </Select>
            <div className="form-hint">You will be prompted to change your password after this period.</div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="loginAttempts">Maximum Login Attempts</label>
            <Select
              id="loginAttempts"
              name="loginAttempts"
              className="rt-select--form"
              value={securitySettings.loginAttempts}
              onChange={handleSelectChange}
            >
              <option value="3">3 attempts</option>
              <option value="5">5 attempts</option>
              <option value="10">10 attempts</option>
            </Select>
            <div className="form-hint">Your account will be temporarily locked after this many failed login attempts.</div>
          </div>

          <div className="settings-option">
            <div>
              <div className="settings-option-label">Remember Device 
                <span style={{ color: 'red', fontWeight: 'bold', marginLeft: '0.5rem', cursor: 'help' }} title="Coming Soon">*</span>
              </div>
              <div className="settings-option-description">Stay logged in on trusted devices</div>
            </div>
            <div className="settings-option-control">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  name="rememberDevice"
                  disabled
                  // checked={securitySettings.rememberDevice}
                  checked={false}
                  onChange={handleToggleChange}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>

        <div className="settings-card">
          <h3 className="settings-card-title">
            Advanced Security
            <span style={{ color: 'red', fontWeight: 'bold', marginLeft: '0.5rem', cursor: 'help' }} title="Coming Soon">*</span>
          </h3>
          <p className="settings-card-description">
            Additional security features for your account.
          </p>

          <div className="settings-option">
            <div>
              <div className="settings-option-label">IP Restriction</div>
              <div className="settings-option-description">Limit access to specific IP addresses</div>
            </div>
            <div className="settings-option-control">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  name="ipRestriction"
                  checked={securitySettings.ipRestriction}
                  onChange={handleToggleChange}
                  disabled
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>

          {securitySettings.ipRestriction && (
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label className="form-label" htmlFor="allowedIPs">Allowed IP Addresses</label>
              <textarea
                id="allowedIPs"
                className="form-input"
                rows="3"
                placeholder="Enter IP addresses, one per line (e.g., 192.168.1.1)"
                disabled
              ></textarea>
              <div className="form-hint">Leave empty to allow all IP addresses.</div>
            </div>
          )}

          <div className="settings-option">
            <div>
              <div className="settings-option-label">Activity Logging</div>
              <div className="settings-option-description">Log all account activity for security monitoring</div>
            </div>
            <div className="settings-option-control">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  name="activityLogging"
                  // checked={securitySettings.activityLogging}
                  checked={false}
                  onChange={handleToggleChange}
                  disabled
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>

        <div className="settings-card">
          <h3 className="settings-card-title">
            Security Log
            <span style={{ color: 'red', fontWeight: 'bold', marginLeft: '0.5rem', cursor: 'help' }} title="Coming Soon">*</span>
          </h3>
          <p className="settings-card-description">
            Recent security events for your account.
          </p>

          <div style={{
            border: '1px solid var(--border-color-light, #e9ecef)',
            borderRadius: '4px'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid var(--border-color-light, #e9ecef)' }}>Date</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid var(--border-color-light, #e9ecef)' }}>Event</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid var(--border-color-light, #e9ecef)' }}>IP Address</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color-light, #e9ecef)' }}>Jul 4, 2025 14:30</td>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color-light, #e9ecef)' }}>Successful login</td>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color-light, #e9ecef)' }}>192.168.1.1</td>
                </tr>
                <tr>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color-light, #e9ecef)' }}>Jul 3, 2025 09:15</td>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color-light, #e9ecef)' }}>Password changed</td>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color-light, #e9ecef)' }}>192.168.1.1</td>
                </tr>
                <tr>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color-light, #e9ecef)' }}>Jul 2, 2025 16:45</td>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color-light, #e9ecef)' }}>Failed login attempt</td>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color-light, #e9ecef)' }}>203.0.113.1</td>
                </tr>
                <tr>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color-light, #e9ecef)' }}>Jul 1, 2025 10:20</td>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color-light, #e9ecef)' }}>Successful login</td>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color-light, #e9ecef)' }}>192.168.1.1</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '1rem' }}>
            <button type="button" className="btn btn-outline" disabled>
              View Full Security Log
            </button>
          </div>
        </div>

        {/* <div className="form-actions">
          <button type="button" className="btn btn-outline">Cancel</button>
          <button type="submit" className="btn btn-primary">Save Changes</button>
        </div> */}
      </form>
    </div>
  );
};

export default SecuritySettings;